import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, Clock, Filter, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    email_type: 'all',
    status: 'all',
    days: '7'
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchData();
  }, [filters, pagination.page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Build query params
      const params = new URLSearchParams();
      params.append('days', filters.days);
      params.append('page', pagination.page);
      params.append('limit', '20');
      if (filters.email_type !== 'all') params.append('email_type', filters.email_type);
      if (filters.status !== 'all') params.append('status', filters.status);
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/emails/logs?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/emails/stats?days=${filters.days}`, { headers })
      ]);
      
      setLogs(logsRes.data.logs || []);
      setPagination(prev => ({
        ...prev,
        ...logsRes.data.pagination
      }));
      setStats(statsRes.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch email data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      course: 'bg-blue-500/20 text-blue-400',
      newsletter: 'bg-green-500/20 text-green-400',
      contact: 'bg-purple-500/20 text-purple-400',
      notification: 'bg-yellow-500/20 text-yellow-400'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  const StatCard = ({ title, value, icon: Icon, color = 'orange' }) => (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
          </div>
          <div className={`p-2 rounded-lg bg-${color}-500/20`}>
            <Icon className={`h-5 w-5 text-${color}-500`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Email Activity Log</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-gray-600 text-gray-300"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Sent Today" value={stats.sent_today} icon={Mail} />
          <StatCard title="Total Sent" value={stats.total_sent} icon={CheckCircle} color="green" />
          <StatCard title="Failed" value={stats.total_failed} icon={XCircle} color="red" />
          <StatCard title="Success Rate" value={`${stats.success_rate}%`} icon={Clock} color="blue" />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-400">Filters:</span>
            </div>
            
            <Select
              value={filters.email_type}
              onValueChange={(v) => {
                setFilters(prev => ({ ...prev, email_type: v }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-36 bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="course">Course</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="contact">Contact</SelectItem>
                <SelectItem value="notification">Notification</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(v) => {
                setFilters(prev => ({ ...prev, status: v }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-32 bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.days}
              onValueChange={(v) => {
                setFilters(prev => ({ ...prev, days: v }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-32 bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="1">Today</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Email Log Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Recent Emails ({pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No emails found for the selected filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Type</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Recipient</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Subject</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-3 px-4">
                          {log.status === 'sent' ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="h-4 w-4" />
                              Sent
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400">
                              <XCircle className="h-4 w-4" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getTypeColor(log.email_type)}>
                            {log.email_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white text-sm">{log.to_name || 'Unknown'}</p>
                            <p className="text-gray-500 text-xs">{log.to_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-white text-sm max-w-xs truncate">
                          {log.subject}
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {formatDate(log.sent_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm text-gray-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      className="border-gray-600 text-gray-300"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      className="border-gray-600 text-gray-300"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailActivityLog;
